import type { DeliveryLocationDto } from '@api/customers'
import type {
  ApiError,
  DealerOptionItemDto,
  EquipmentListItemDto,
  FactoryOptionDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineItemPriceOnlyDto,
  QuotationDto,
  QuotationMetatadaTableDto
} from '@api/machines'
import { errorToast } from '@components/reusable'
import i18n from 'i18next'

import translations from '../../i18n/locales/en/gb/common-translations-gb.json'
import { APPROVAL_STATUSES, falseString, OTHER, trueString } from '../constants'
import { TRIGGER_OPEN_CONTINUE_SESSION_DIALOG } from '../customEventsConstants'
import { Options, QuoteActions, QuoteStatus } from '../enums'
import type { QuoteAction } from '../types'

// Global flag to track if we're in initial authentication phase
declare global {
  interface Window {
    __isAuthenticating?: boolean
  }
}

export const envCurrency = import.meta.env.NX_ENV_CURRENCY

export const envRegion = import.meta.env.NX_ENV_REGION

const enableDecimals = import.meta.env.NX_ENV_DECIMALS_ENABLED === 'true'

const newEnvRegion =
  localStorage.getItem('preferredRegion') ?? envRegion ?? 'en-GB'
const newEnvCurrencyCode =
  localStorage.getItem('preferredCurrencyCode') ?? envCurrency ?? 'GBP'
const newEnvCurrencySymbol =
  localStorage.getItem('preferredCurrencySymbol') ?? '£'

const newEnvDateFormat =
  localStorage.getItem('preferredDateFormat') ?? 'DD/MM/YYYY'

export const getEnvInputSeparators = () => {
  switch (newEnvRegion) {
    case 'en-GB':
    case 'en-US':
      return { decimal: '.', thousands: ',' }
    default:
      return { decimal: '.', thousands: ',' }
  }
}

export function groupBy<T, K extends keyof T>(
  data: T[],
  field: K
): { [key: string]: T[] } {
  const resultMap = new Map()

  for (const item of data) {
    const keyField = item[field]

    if (keyField && keyField !== '') {
      const existingGroup = resultMap.get(keyField)

      if (existingGroup) {
        existingGroup.push(item)
      } else {
        resultMap.set(keyField, [item])
      }
    }
  }

  return Object.fromEntries(resultMap) as { [key: string]: T[] }
}

export const splitFactoryOptionsItems = (
  items: FactoryOptionDto[],
  withFamily: boolean
): FactoryOptionDto[] => {
  if (withFamily) {
    return items.filter(item => item.family !== OTHER)
  }
  return items.filter(item => item.family === OTHER)
}

export function calculateQuotesQuantity(data: QuotationDto[]) {
  return data.map(machineData => {
    let totalQuantity = 0

    if (machineData?.machineItems) {
      totalQuantity += machineData.machineItems.reduce(
        (acc, item) => acc + (item.quantity ?? 0),
        0
      )
    }

    if (machineData?.machineAttachmentItems) {
      totalQuantity += machineData.machineAttachmentItems.reduce(
        (acc, item) => acc + (item.quantity ?? 0),
        0
      )
    }

    const resultData = {
      ...machineData,
      quantity: totalQuantity
    }

    return resultData
  })
}

export const getEnvDateFormat = (gridFormat = false) => {
  if (gridFormat) {
    return newEnvDateFormat
  }

  switch (newEnvRegion) {
    case 'en-GB':
      return 'DD/MM/YYYY'
    case 'en-US':
      return 'MM/DD/YYYY'
    default:
      return 'DD/MM/YYYY'
  }
}

export const getEnvRegion = () => {
  return newEnvRegion
}

const getDecimalPlaces = ({
  hasDecimals,
  fractionalDigitsCount
}: {
  hasDecimals: boolean
  fractionalDigitsCount: number
}): number => {
  if (!hasDecimals) {
    return 0
  }
  return fractionalDigitsCount === 0 ? 2 : fractionalDigitsCount
}

export const formatDateAndTime = (
  dateString: string,
  returnOptions: { date: boolean; time: boolean }
): string => {
  const defaultDateOptions: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  }
  const defaultTimeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }

  const date = new Date(dateString)

  if (returnOptions?.date && returnOptions?.time) {
    return date.toLocaleString(newEnvRegion, {
      ...defaultDateOptions,
      ...defaultTimeOptions
    })
  }
  if (returnOptions?.date && returnOptions?.time) {
    return date.toLocaleTimeString(newEnvRegion, defaultTimeOptions)
  }
  return date.toLocaleDateString(newEnvRegion, defaultDateOptions)
}

export const getFeatureFlagsObject = (
  input: string
): { [key: string]: boolean } => {
  const result: { [key: string]: boolean } = {}

  if (!input.trim()) {
    return result
  }

  const pairs = input.split(',')

  for (const pair of pairs) {
    const [key, value] = pair.split('=').map(str => str.trim())

    if (key && value === 'true') {
      result[key] = true
    } else if (key && value === 'false') {
      result[key] = false
    }
  }

  return result
}

export const getEnvCurrencyFromData = () => {
  return newEnvCurrencySymbol
}

export const formatPriceAndCurrency = (
  price: null | number | undefined,
  fractionalDigitsCount = 0
) => {
  const safePrice = price ?? 0

  // If decimals are disabled, truncate (floor) the decimal part instead of rounding
  const priceToFormat = enableDecimals ? safePrice : Math.floor(safePrice)

  // Check if the number has meaningful decimal places (not just .00)
  const hasDecimals = priceToFormat % 1 !== 0

  let finalDigitsAfterComma = 0
  if (enableDecimals) {
    if (hasDecimals) {
      finalDigitsAfterComma =
        fractionalDigitsCount === 0 ? 2 : fractionalDigitsCount
    }
  }

  const formattedPrice = priceToFormat.toLocaleString(newEnvRegion, {
    currency: newEnvCurrencyCode,
    maximumFractionDigits: finalDigitsAfterComma,
    minimumFractionDigits: finalDigitsAfterComma,
    style: 'currency'
  })

  return formattedPrice
}

export const formatPercentages = (
  value: null | number | undefined,
  fractionalDigitsCount = 0
) => {
  const safeValue = value ?? 0

  // Check if the number has meaningful decimal places (not just .00)
  const hasDecimals = safeValue % 1 !== 0

  const finalDigitsAfterComma = getDecimalPlaces({
    hasDecimals,
    fractionalDigitsCount
  })

  return `${safeValue.toLocaleString(envRegion, {
    maximumFractionDigits: finalDigitsAfterComma,
    minimumFractionDigits: finalDigitsAfterComma
  })} %`
}

export const formatRegularNumbers = (value: null | number | undefined) => {
  let safeValue = value ?? 0

  if (!value) {
    safeValue = 0
  }

  // If decimals are disabled, truncate (floor) the decimal part instead of rounding
  const valueToFormat = enableDecimals ? safeValue : Math.floor(safeValue)

  // Check if the number has meaningful decimal places (not just .00)
  const hasDecimals = valueToFormat % 1 !== 0

  // For numbers with decimals, show up to 2 decimal places
  // For whole numbers, always use 0 decimal places to avoid showing .00
  let finalDigitsAfterComma = 0
  if (enableDecimals && hasDecimals) {
    finalDigitsAfterComma = 2
  }

  return valueToFormat.toLocaleString(envRegion, {
    maximumFractionDigits: finalDigitsAfterComma,
    minimumFractionDigits: finalDigitsAfterComma
  })
}

// Track the last time a 401 error was handled to prevent multiple dialogs
let last401Time = 0
const DEBOUNCE_401_MS = 2000 // Don't show dialog more than once per 2 seconds

const triggerOpenContinuationSessionDialog = () => {
  const event = new CustomEvent(TRIGGER_OPEN_CONTINUE_SESSION_DIALOG)

  globalThis.dispatchEvent(event)
}

export const handleCatchError = (e: unknown) => {
  const error = e as ApiError
  if (error.status === 401) {
    // Don't show session expired dialog during initial authentication
    if ((globalThis as any).__isAuthenticating) {
      console.warn('401 error suppressed - authentication in progress')
      return
    }

    // Debounce 401 errors to prevent multiple dialogs during authentication race conditions
    const now = Date.now()
    if (now - last401Time < DEBOUNCE_401_MS) {
      console.warn('401 error debounced - too soon after previous 401')
      return
    }
    last401Time = now

    triggerOpenContinuationSessionDialog()
    return
  }

  const translationsFile = translations.common.apiErrorMessages

  const errorObject = error.body

  let localizationKey: string | undefined
  let localizationData: string | undefined
  try {
    const errorData = JSON.parse(errorObject)
    localizationKey = errorData.localizationKey
    localizationData = errorData.localizationData
  } catch {
    errorToast(i18n.t('common.apiErrorMessages.genericError'))
    return
  }

  const translationRoot = 'common.apiErrorMessages'
  const identifier = `${translationRoot}.${localizationKey}`
  const translationVars = (localizationData as string) ?? {}

  const keyExists =
    localizationKey !== undefined && localizationKey in translationsFile

  const toastMessage = keyExists
    ? i18n.t(identifier, translationVars)
    : i18n.t(errorObject.detail)

  errorToast(toastMessage)
}

export type NameItemType = null | string | undefined

export function cleanName(name: NameItemType): string {
  if (!name) {
    return ''
  }
  return name.trim().toLowerCase()
}

export function nameExists(
  items?: NameItemType[],
  nameToCheck?: string
): boolean {
  if (!(nameToCheck && items)) {
    return false
  }

  const cleanedNames = items.map(item => cleanName(item))
  const cleanedNameToCheck = cleanName(nameToCheck)

  return cleanedNames.includes(cleanedNameToCheck)
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const isObjectEmpty = (obj: Record<string, any>) => {
  return Object.keys(obj).length === 0
}

export const getUserProfileFromLocalStorage = () => {
  const identityString = sessionStorage.getItem(
    `oidc.user:${
      // biome-ignore lint/complexity/useLiteralKeys: <explanation>
      import.meta.env['NX_AUTHORITY']
    }:${
      // biome-ignore lint/complexity/useLiteralKeys: <explanation>
      import.meta.env['NX_CLIENT_ID']
    }`
  )

  if (!identityString) {
    return undefined
  }

  const identityObject = JSON.parse(identityString)
  return identityObject.profile
}

export const formatAddress = (option: DeliveryLocationDto) => {
  const addressParts = [
    option?.companyNickname,
    option?.address1,
    option?.address2,
    option?.address3,
    option?.city,
    option?.country
  ]

  return addressParts.filter(Boolean).join(', ')
}

export const displayOldPrice = (
  currentPrice: number | undefined,
  oldPrice: number | undefined
) => {
  // If we don't have an old price (undefined/null/NaN), do not mark as changed
  if (oldPrice === undefined || oldPrice === null || Number.isNaN(oldPrice)) {
    return undefined
  }

  // If current price is missing, also avoid showing a change
  if (
    currentPrice === undefined ||
    currentPrice === null ||
    Number.isNaN(currentPrice)
  ) {
    return undefined
  }

  return currentPrice === oldPrice ? undefined : oldPrice
}

export const matchOldPriceItem = (
  oldPricesObj?: MachineAttachmentItemPriceOnlyDto | MachineItemPriceOnlyDto,
  item?: {
    id: number
    price: number
    type: 'baseUnit' | 'dealerOptions' | 'factoryOptionItems'
  }
) => {
  if (!item) {
    return undefined
  }

  const itemWithOldPrice = oldPricesObj?.[item.type]?.find(
    oldPriceItem => oldPriceItem.id === item.id
  )

  const itemDnp = itemWithOldPrice?.dnp ?? undefined

  return itemDnp === undefined
    ? undefined
    : displayOldPrice(item.price, itemDnp)
}

export const calculateDealerOptionsSectionTotal = (
  items: DealerOptionItemDto[]
) => {
  return items
    .filter(item => !item.isArchived)
    .reduce(
      (acc, currentVal) =>
        acc + (currentVal.quantity ?? 0) * (currentVal.dnp ?? 0),
      0
    )
}

export const archiveRestoreEquipments = (
  currentList: EquipmentListItemDto[],
  updatedItemsIds: number[]
): EquipmentListItemDto[] => {
  return currentList.map(equipment =>
    updatedItemsIds.includes(equipment.id as number)
      ? { ...equipment, isArchived: !equipment.isArchived }
      : equipment
  )
}

export const hideRestoreEquipments = (
  equipmentList: EquipmentListItemDto[],
  itemIds: number[],
  setHidden = true
): EquipmentListItemDto[] => {
  if (!equipmentList || itemIds.length === 0) {
    return equipmentList
  }

  return equipmentList.map(equipment => {
    if (itemIds.includes(equipment.id as number)) {
      return {
        ...equipment,
        isHidden: setHidden
      }
    }

    return equipment
  })
}

export const syntheticMappedArrays = <T>(arr: T[]): (T & { id: number })[] => {
  return arr.map((item, index) => ({ ...item, id: index }))
}

export const isIdMissing = (id: number | undefined | null) => {
  return id === null || id === undefined
}

export function machineNamesToString(
  names: string[],
  maxLength: number,
  count: number
): [string, number, string[]] {
  if (names.length === 0) {
    return ['', 0, []]
  }

  let charsUsed = 0
  let itemCount = 0

  for (const name of names) {
    if (charsUsed + name.length > maxLength) {
      break
    }

    charsUsed += name.length + 2
    itemCount++
  }

  const shownNames = names.slice(0, itemCount).join(', ')
  const shownNamesArray = names.slice(0, itemCount)
  const remainingCount = shownNames ? count - itemCount : count - itemCount - 1

  return [shownNames || names[0], remainingCount, shownNamesArray]
}

export function getTruncatedLabelForItemsColumn(
  items: string[],
  maxNoOfItemsDisplayed: number
) {
  let textStr = ''
  const itemsToDisplay = Math.min(items.length, maxNoOfItemsDisplayed)

  for (let i = 0; i < itemsToDisplay; i++) {
    textStr += items[i]
    if (i < itemsToDisplay - 1) {
      textStr += ', '
    }
  }

  return textStr
}

export const getFilteredBccs = (bccs: string, salesManagerEmail: string) => {
  return bccs
    .split(';')
    .filter((email: string) => email !== salesManagerEmail && email !== '')
}

export const mergeWithCompanyName = <T>(
  items: T[] | null | undefined,
  companyName: string
): T[] => (items || []).map(item => ({ ...item, companyName }))

export const isValidDate = (date: string) => {
  if (!Number.isNaN(Number(date))) {
    return false
  }

  const newDate = new Date(date)
  return !Number.isNaN(newDate.getTime())
}

export const getBooleanString = (value: boolean | null) => {
  if (value === true) {
    return trueString
  } else if (value === false) {
    return falseString
  }
  return null
}

export const filterQuotesByAction = (
  quotes: QuotationMetatadaTableDto[],
  action: QuoteAction
): QuotationMetatadaTableDto[] => {
  switch (action) {
    case QuoteActions.ARCHIVE:
      return quotes.filter(
        item =>
          item.status !== QuoteStatus.ARCHIVED &&
          !APPROVAL_STATUSES.includes(item.status as QuoteStatus)
      )
    case QuoteActions.RESTORE:
      return quotes.filter(item => item.status === QuoteStatus.ARCHIVED)
    case QuoteActions.DELETE:
      return quotes.filter(
        item => !APPROVAL_STATUSES.includes(item.status as QuoteStatus)
      )
    default:
      return quotes
  }
}

export const validateEmail = (email: string) => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
    email
  )
}

export const getOptionByType = (type: string, isWithinMachine: boolean) => {
  const optionMap = {
    dealer: isWithinMachine
      ? Options.REMOVE_DEALER_FIT_WITHIN_MACHINE
      : Options.REMOVE_DEALER_FIT,
    factory: isWithinMachine
      ? Options.REMOVE_FACTORY_FIT_WITHIN_MACHINE
      : Options.REMOVE_FACTORY_FIT,
    serviceContract: Options.REMOVE_SERVICE_CONTRACT,
    warranty: isWithinMachine
      ? Options.REMOVE_WARRANTY_WITHIN_MACHINE
      : Options.REMOVE_WARRANTY,
    transport: isWithinMachine
      ? Options.REMOVE_TRANSPORT_OPTION_WITHIN_MACHINE
      : Options.REMOVE_TRANSPORT_OPTION
  }
  return optionMap[type as keyof typeof optionMap]
}

export const optionChangeType = (option: Options) => {
  const addFactoryFit = option === Options.ADD_FACTORY_FIT
  const addDealerFit = option === Options.ADD_DEALER_FIT

  const addOrRemoveForNestedAttachment =
    option === Options.ADD_WARRANTY_WITHIN_MACHINE ||
    option === Options.ADD_TRANSPORT_OPTION_WITHIN_MACHINE ||
    option === Options.REMOVE_WARRANTY_WITHIN_MACHINE ||
    option === Options.REMOVE_FACTORY_FIT_WITHIN_MACHINE ||
    option === Options.REMOVE_TRANSPORT_OPTION_WITHIN_MACHINE ||
    option === Options.REMOVE_DEALER_FIT_WITHIN_MACHINE

  const addFactoryFitForNestedAttachment =
    option === Options.ADD_FACTORY_FIT_WITHIN_MACHINE

  const addDealerFitForNestedAttachment =
    option === Options.ADD_DEALER_FIT_WITHIN_MACHINE

  return {
    addFactoryFit,
    addDealerFit,
    addFactoryFitForNestedAttachment,
    addDealerFitForNestedAttachment,
    addOrRemoveForNestedAttachment
  }
}

export const isHtmlEditorEmpty = (html: string) => {
  if (!html) {
    return true
  }

  const div = document.createElement('div')
  div.innerHTML = html

  const text = div.textContent || div.innerText || ''
  const normalized = text.replaceAll('\u00A0', ' ').trim()

  return normalized.length === 0
}
